import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface WorkerRiskData {
  id: string;
  display_name: string;
  company_name: string;
  current_risk_score: number;
  baseline_risk_score: number;
  network_role: string | null;
  network_centrality: number | null;
  clustering_coefficient: number | null;
  psychometric_profile: any;
}

const SuperAdminBehavioral = () => {
  const [workers, setWorkers] = useState<WorkerRiskData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalWorkers: 0,
    highRiskWorkers: 0,
    avgRiskScore: 0,
    champions: 0,
    bridges: 0,
    isolates: 0,
  });

  useEffect(() => {
    fetchBehavioralData();
  }, []);

  const fetchBehavioralData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('worker_profiles')
        .select(`
          id, display_name, current_risk_score, baseline_risk_score,
          network_role, network_centrality, clustering_coefficient,
          psychometric_profile, company_id,
          companies!worker_profiles_company_id_fkey(company_name)
        `)
        .order('current_risk_score', { ascending: false })
        .limit(100);

      if (error) throw error;

      const workersData = (data || []).map((w: any) => ({
        ...w,
        company_name: w.companies?.company_name || 'Unknown',
      }));

      setWorkers(workersData);

      // Calculate stats
      const totalWorkers = workersData.length;
      const highRiskWorkers = workersData.filter(w => w.current_risk_score >= 60).length;
      const avgRiskScore = totalWorkers > 0 
        ? Math.round(workersData.reduce((sum, w) => sum + w.current_risk_score, 0) / totalWorkers)
        : 0;
      const champions = workersData.filter(w => w.network_role === 'champion').length;
      const bridges = workersData.filter(w => w.network_role === 'bridge').length;
      const isolates = workersData.filter(w => w.network_role === 'isolate').length;

      setStats({ totalWorkers, highRiskWorkers, avgRiskScore, champions, bridges, isolates });
    } catch (error) {
      console.error('Error fetching behavioral data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleBadge = (role: string | null) => {
    switch (role) {
      case 'champion':
        return <Badge className="bg-success">Champion</Badge>;
      case 'bridge':
        return <Badge className="bg-primary">Bridge</Badge>;
      case 'learner':
        return <Badge variant="secondary">Learner</Badge>;
      case 'isolate':
        return <Badge variant="destructive">Isolate</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getRiskBadge = (score: number) => {
    if (score >= 75) return <Badge variant="destructive">{score}</Badge>;
    if (score >= 50) return <Badge className="bg-warning text-warning-foreground">{score}</Badge>;
    return <Badge variant="secondary">{score}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Platform Behavioral Data</h2>
        <p className="text-muted-foreground">Risk scores and network analysis across all companies</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Workers</p>
            <p className="text-2xl font-bold">{stats.totalWorkers}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">High Risk</p>
            <p className="text-2xl font-bold text-destructive">{stats.highRiskWorkers}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Avg Risk Score</p>
            <p className="text-2xl font-bold">{stats.avgRiskScore}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Champions</p>
            <p className="text-2xl font-bold text-success">{stats.champions}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Bridges</p>
            <p className="text-2xl font-bold text-primary">{stats.bridges}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Isolates</p>
            <p className="text-2xl font-bold text-destructive">{stats.isolates}</p>
          </CardContent>
        </Card>
      </div>

      {/* Workers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Worker Risk & Network Data</CardTitle>
          <CardDescription>Top 100 workers by risk score</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Current Risk</TableHead>
                  <TableHead>Baseline Risk</TableHead>
                  <TableHead>Network Role</TableHead>
                  <TableHead>Centrality</TableHead>
                  <TableHead>Clustering</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : workers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No workers found
                    </TableCell>
                  </TableRow>
                ) : (
                  workers.map((worker) => (
                    <TableRow key={worker.id}>
                      <TableCell className="font-medium">{worker.display_name}</TableCell>
                      <TableCell>{worker.company_name}</TableCell>
                      <TableCell>{getRiskBadge(worker.current_risk_score)}</TableCell>
                      <TableCell>{worker.baseline_risk_score}</TableCell>
                      <TableCell>{getRoleBadge(worker.network_role)}</TableCell>
                      <TableCell>
                        {worker.network_centrality !== null 
                          ? worker.network_centrality.toFixed(2) 
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {worker.clustering_coefficient !== null 
                          ? worker.clustering_coefficient.toFixed(2) 
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminBehavioral;